import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { withAttribution } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Shared fixed-quote form block (service + discipline pages). */
export default function QuoteBlock({ serviceSlug, serviceName, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", project_details: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_details) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, withAttribution({ ...form, service_slug: serviceSlug, source: `quote-form-${serviceSlug}` }));
      toast.success(`Quote request sent for ${serviceName}. We'll reply within 1 business day.`);
      setForm({ name: "", email: "", project_details: "" });
      onSuccess && onSuccess();
    } catch (err) {
      toast.error("Could not submit. Please try again or email triaxisxyzsolutions@gmail.com");
    } finally { setLoading(false); }
  };

  return (
    <section className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Request a quote</div>
          <h2 className="display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-slate-900">
            Drop the brief — get a quote in <span className="text-emerald-700">under 24 hours</span>.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Share scope, deadline, and your CAD standards. We'll respond with a fixed quote and proposed delivery date.
          </p>
        </div>
        <form onSubmit={submit} data-testid="service-quote-form" className="lg:col-span-7 border border-slate-200 p-6 lg:p-8 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" id="quote-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="quote-input-name" />
            <Field label="Work email" id="quote-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="quote-input-email" />
          </div>
          <div className="mt-4">
            <label htmlFor="quote-details" className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Project details</label>
            <textarea
              id="quote-details"
              data-testid="quote-input-details"
              rows={5}
              value={form.project_details}
              onChange={(e) => setForm({ ...form, project_details: e.target.value })}
              className="mt-2 w-full border border-slate-300 focus:border-topo outline-none p-3 text-sm rounded-sm bg-white"
              placeholder="Scope, file formats, CAD standards, deadline..."
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit-button"
            className="mt-5 inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark disabled:opacity-60 text-white text-sm font-semibold px-5 py-3"
          >
            <Send className="h-4 w-4" /> {loading ? "Sending..." : "Send Quote Request"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, id, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label htmlFor={id} className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full border border-slate-300 focus:border-topo outline-none p-3 text-sm rounded-sm bg-white"
      />
    </div>
  );
}
